'use client';

import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { HTMLAttributes, useEffect, useMemo, useRef } from 'react';

type LottieComponentProps = {
  src?: string;
  data?: string;
  loop?: boolean;
  autoplay?: boolean;
  className?: string;
  style?: HTMLAttributes<HTMLDivElement>['style'];
};

/** Convert lottie.host embed URLs to direct file URLs the player can fetch. */
const normalizeLottieUrl = (url: string): string => {
  let normalized = url.startsWith('//') ? `https:${url}` : url;
  normalized = normalized.replace(/lottie\.host\/embed\//, 'lottie.host/');
  return normalized;
};

function LottieFromJson({
  data,
  loop,
  autoplay,
  className,
  style,
}: {
  data: string;
  loop: boolean;
  autoplay: boolean;
  className?: string;
  style?: HTMLAttributes<HTMLDivElement>['style'];
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<ReturnType<
    (typeof import('lottie-web'))['default']['loadAnimation']
  > | null>(null);
  const animationData = useMemo(() => {
    try {
      const cleaned = data.replace(/[\x00-\x1F\x7F]/g, '');
      return JSON.parse(cleaned);
    } catch {
      return null;
    }
  }, [data]);

  useEffect(() => {
    if (!containerRef.current || !animationData) return;

    // Clean up any previous animation
    if (animRef.current) {
      animRef.current.destroy();
      animRef.current = null;
    }
    // Clear container to prevent duplicate SVGs
    containerRef.current.innerHTML = '';

    let cancelled = false;

    import('lottie-web').then((lottieModule) => {
      if (cancelled || !containerRef.current) return;
      const lottie = lottieModule.default;
      animRef.current = lottie.loadAnimation({
        container: containerRef.current,
        renderer: 'svg',
        loop,
        autoplay,
        animationData,
      });
    });

    return () => {
      cancelled = true;
      if (animRef.current) {
        animRef.current.destroy();
        animRef.current = null;
      }
    };
  }, [animationData, loop, autoplay]);

  if (!animationData) return null;
  return <div ref={containerRef} className={className} style={{ width: '100%', ...style }} />;
}

const LottieComponent = ({
  src,
  data,
  loop = true,
  autoplay = true,
  className,
  style,
}: LottieComponentProps) => {
  if (data) {
    return (
      <LottieFromJson
        data={data}
        loop={loop}
        autoplay={autoplay}
        className={className}
        style={style}
      />
    );
  }

  const normalizedSrc = src ? normalizeLottieUrl(src) : '';
  return (
    <DotLottieReact
      src={normalizedSrc}
      loop={loop}
      autoplay={autoplay}
      className={className}
      style={style}
    />
  );
};

export default LottieComponent;
